import { type FC, useEffect } from 'react';

import classNames from 'classnames';

import { useAppSelector } from '../../store/react_redux_typed';

import styles from './password_strength.module.css';

type Props = {
  password: string;
  onValidate?: (isValid: boolean) => void;
  className?: string;
};

type Rule = {
  id: 'min_length' | 'lowercase' | 'uppercase' | 'digits' | 'special_symbols';
  message: string;
  validator: (password: string) => boolean;
};

const getRules = (minLength: number): Rule[] => [
  {
    id: 'min_length',
    message: `Minimum ${minLength} characters`,
    validator: (password) => password.length >= minLength,
  },
  {
    id: 'uppercase',
    message: 'At least 1 upper case letter',
    validator: (password) => /[A-Z]/.test(password),
  },
  {
    id: 'lowercase',
    message: 'At least 1 lower case letter',
    validator: (password) => /[a-z]/.test(password),
  },
  {
    id: 'digits',
    message: 'At least 1 number',
    validator: (password) => /\d+/.test(password),
  },
  {
    id: 'special_symbols',
    message: 'At least 1 special character',
    validator: (password) => /[!@#$%^&*]/.test(password),
  },
];

const PasswordStrength: FC<Props> = ({ className, onValidate, password }) => {
  const passwordPolicy = useAppSelector(
    (state) => state.settings.merged.password_checks,
  );
  const minLength = passwordPolicy?.min_length ?? 8;
  const rules = getRules(minLength);

  useEffect(() => {
    const score = rules.reduce(
      (acc, rule) => acc + (rule.validator(password) ? 1 : 0),
      0,
    );
    onValidate?.(score === rules.length);
  }, [password, onValidate, rules]);

  return (
    <div className={classNames(styles.root, className)}>
      <ul className={styles.checklist}>
        {rules.map((rule) => (
          <li key={rule.id} className={styles.rule}>
            <div className={styles.rule_status}>
              <div
                className={classNames(
                  styles.circle,
                  rule.validator(password) ? styles.valid : null,
                )}
              />
            </div>
            <div>
              {rule.message}
              {passwordPolicy?.[rule.id] ? ' *' : ''}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PasswordStrength;
